// Order/PromoCodeField.tsx

import React, {useEffect, useRef, useState} from 'react';
import debounce from 'lodash.debounce';
import {FC} from "WideLayout/Layouts";
import {axios} from "Auth/axiosConfig";
import CircularProgress from "Core/components/elements/CircularProgress";
import {TextField} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {useTheme} from "Theme/ThemeContext";
import {useErrorProcessing} from "Core/components/ErrorProvider";
import {Message} from "Core/components/Message";
import {IPromocode} from "Types/commerce/promocode";
import {ICurrency} from "Types/commerce/shop";

interface PromoCodeFieldProps {
    cls?: string;
    psychologistId?: number;
    productId: number;
    currency: ICurrency;
    onValidChange?: (isValid: boolean | null, promocode?: IPromocode) => void;
    onPromoCodeChange?: (code: string) => void;
    revalidateKey?: number;
}

const PromoCodeField: React.FC<PromoCodeFieldProps> = (
    {
        cls,
        psychologistId,
        productId,
        currency,
        onValidChange,
        onPromoCodeChange,
        revalidateKey
    }) => {
    const [promoCode, setPromoCode] = useState<string>('');
    const [status, setStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
    const promoCodeRef = useRef<HTMLInputElement>(null);

    const {plt} = useTheme();
    const {byResponse} = useErrorProcessing();

    const checkPromoCode = debounce(async (code: string) => {
        if (!code) {
            setStatus('idle');
            if (onValidChange) onValidChange(null);
            return;
        }
        setStatus('checking');
        try {
            const response = await axios.post('api/v1/promocode/applicable/', {
                promocode: code,
                psychologist: psychologistId,
                product: productId,
                currency,
            });
            setStatus('valid');
            if (promoCodeRef.current) promoCodeRef.current.blur();
            if (onValidChange) onValidChange(true, response.data);
            Message.success('Отлично, ваш промокод подходит!');
        } catch (error) {
            byResponse(error);
            setStatus('invalid');
            if (onValidChange) onValidChange(false);
        }
    }, 2000);

    useEffect(() => {
        checkPromoCode(promoCode);
        return () => checkPromoCode.cancel();
    }, [promoCode]);

    // Если revalidateKey изменился и есть промокод, перевалидация
    useEffect(() => {
        if (revalidateKey !== undefined && revalidateKey !== null) {
            if (promoCode) {
                checkPromoCode(promoCode);
            }
        }
    }, [revalidateKey]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase(); // Преобразование в верхний регистр
        setPromoCode(value);
        if (value) {
            setStatus('checking');
        } else {
            setStatus('idle');
            if (onValidChange) onValidChange(null);
        }
        if (onValidChange) onValidChange(null);
        if (onPromoCodeChange) onPromoCodeChange(value);
    };

    const getColor = () => {
        if (status === 'valid') return 'success';
        if (status === 'invalid') return 'error';
        return 'default';
    };

    return (
        <FC w={'100%'} pos={'relative'} cls={cls}>
            <TextField
                label="Промокод"
                variant={'filled'}
                value={promoCode}
                onChange={handleChange}
                fullWidth
                color={getColor() as any}
                inputRef={promoCodeRef}
            />
            {status === 'checking' && (
                <CircularProgress size={'2rem'} sx={{
                    color: plt.colors.secondary.main,
                    position: 'absolute',
                    top: 'calc(50% - 1rem)',
                    right: '10px',
                }}/>
            )}
            {status === 'valid' && (
                <CheckCircleOutlineIcon
                    style={{
                        color: plt.colors.secondary.main,
                        position: 'absolute',
                        top: '50%',
                        right: '10px',
                        transform: 'translateY(-50%)'
                    }}
                />
            )}
            {status === 'invalid' && (
                <HighlightOffIcon
                    style={{
                        color: plt.colors.error.main,
                        position: 'absolute',
                        top: '50%',
                        right: '10px',
                        transform: 'translateY(-50%)'
                    }}
                />
            )}
        </FC>
    );
};

export default PromoCodeField;